import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';

const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  const { hostname, protocol } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'http://localhost:3000';
  return `${protocol}//${hostname}`;
};

const Billing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${getApiUrl()}/api/payments/subscription?cache=${Date.now()}`, {
          headers: authHeaders(),
        });
        if (res.ok) setSub(await res.json());
      } catch (_) {
        /* non-fatal; fall back to user.subscription */
      } finally {
        setLoading(false);
      }
    };
    load();
    // Surface the Stripe checkout return state.
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') === 'success') {
      toast.success('Welcome to Premium! Your subscription is active.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const premium = sub ? sub.premium : (user?.subscription || 'free').toLowerCase() === 'premium';
  const planLabel = premium ? 'Premium' : 'Free';

  const openPortal = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/payments/create-portal-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
      });
      const data = await res.json();
      if (!res.ok || !data.url) throw new Error(data.message || 'Could not open billing portal.');
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.message);
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Billing</h1>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Current Plan</p>
              <p className={`text-2xl font-bold ${premium ? 'text-blue-600' : 'text-gray-900'}`}>
                {planLabel}
              </p>
              {sub?.status && (
                <p className="text-xs text-gray-400 mt-1">Status: {sub.status}</p>
              )}
            </div>
            {premium ? (
              <span className="inline-flex px-3 py-1 text-sm font-semibold rounded-full bg-blue-100 text-blue-800">
                Active
              </span>
            ) : (
              <span className="inline-flex px-3 py-1 text-sm font-semibold rounded-full bg-gray-100 text-gray-700">
                Free
              </span>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          {premium ? (
            <>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Manage Subscription</h2>
              <p className="text-sm text-gray-600 mb-4">
                Update your payment method, view invoices, or cancel your subscription in the
                secure Stripe billing portal.
              </p>
              <button
                onClick={openPortal}
                disabled={busy}
                className="px-5 py-2.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50"
              >
                {busy ? 'Opening…' : 'Manage Subscription / Payment Method'}
              </button>
            </>
          ) : (
            <>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Upgrade to Premium</h2>
              <p className="text-sm text-gray-600 mb-4">
                Unlock every pick, AI risk reviews, and full analysis for $99/year.
              </p>
              <button
                onClick={() => navigate('/pricing')}
                className="px-5 py-2.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700"
              >
                View Premium
              </button>
            </>
          )}
        </div>

        <p className="text-xs text-gray-400 mt-6">
          Payments are securely processed by Stripe. We never store your card details.
        </p>
      </div>
    </div>
  );
};

export default Billing;
